//------------------------------------------------------------------------------
// name: useLocationState.ts
// author: mudas
// created: 2025/12/04
//------------------------------------------------------------------------------

import { useEffect } from 'react';
import { Location, useLocation, useNavigate } from 'react-router';
import { useImmer } from 'use-immer';

/**
 * 简化 location state 读写的 hook
 * @returns 包含 state 读取、清除方法和设置新 state 的函数
 */
export const useLocationState = <T extends Record<string, any> = Record<string, any>>(value?: Location<T>) => {
  const location = value || (useLocation() as Location<T>);
  const navigate = useNavigate();
  const [state, setState] = useImmer<T>(location.state || ({} as T));

  /**
   * 清除 location state
   * @param keys 要清除的 state 键名数组，不传则清除所有 state
   */
  const clearLocationState = (keys?: (keyof T)[]) => {
    const currentState = location.state || ({} as T);
    let newState: T;

    if (keys && keys.length > 0) {
      // 只清除指定的键
      newState = { ...currentState };
      keys.forEach((key) => {
        delete newState[key];
      });
    } else {
      // 清除所有 state
      newState = {} as T;
    }

    // 使用 replace 方法更新 history，避免产生新的历史记录
    navigate(location.pathname + location.search, {
      replace: true,
      state: newState,
    });

    // 更新本地 state
    setState(newState);
  };

  /**
   * 设置新的 location state
   * @param newState 要设置的新 state
   * @param replace 是否替换当前历史记录，默认为 true
   */
  const setLocationState = (newState: Partial<T>, replace: boolean = true) => {
    const mergedState = { ...state, ...newState };

    navigate(location.pathname + location.search, {
      replace,
      state: mergedState,
    });

    setState(mergedState as T);
  };

  // 监听 location.state 的变化
  useEffect(() => {
    if (location.state && Object.keys(location.state).length) {
      setState((draft) => {
        Object.assign(draft, location.state);
      });
    }
  }, [location.state]);

  return {
    state,
    clearLocationState,
    setLocationState,
  };
};
